import { useMemo } from 'react';
import type { Action, ForeachAction, UntilAction, AgentTool } from '@/types/workflowDefinition';
import {
  isForeachAction,
  isIfAction,
  isSwitchAction,
  isUntilAction,
  isAgentAction,
} from '@/types/workflowDefinition';
import { useWorkflowStore } from '@/stores/workflowStore';

export interface ForEachConstraintResult {
  /** Whether the ForEach has any child action */
  hasAction: boolean;
  /** Whether a new action can be added (true only if hasAction is false) */
  canAddAction: boolean;
  /** The name of the existing child action, if any */
  childActionName: string | undefined;
  /** Number of child actions currently in the ForEach */
  childActionCount: number;
}

const EMPTY_CONSTRAINT: ForEachConstraintResult = {
  hasAction: false,
  canAddAction: false,
  childActionName: undefined,
  childActionCount: 0,
};

/**
 * Find a ForEach or Until action by name, searching recursively through nested scopes.
 * Returns the action if found, or undefined. Both loop types have an `actions` field.
 */
const findForeachAction = (
  actions: Record<string, Action>,
  targetName: string
): ForeachAction | UntilAction | undefined => {
  for (const [name, action] of Object.entries(actions)) {
    if (name === targetName && (isForeachAction(action) || isUntilAction(action))) {
      return action;
    }

    // Recurse into nested scopes
    if (isForeachAction(action) && action.actions) {
      const found = findForeachAction(action.actions, targetName);
      if (found) return found;
    }

    if (isIfAction(action)) {
      if (action.actions) {
        const found = findForeachAction(action.actions, targetName);
        if (found) return found;
      }
      if (action.else?.actions) {
        const found = findForeachAction(action.else.actions, targetName);
        if (found) return found;
      }
    }

    if (isSwitchAction(action) && action.cases) {
      for (const caseEntry of Object.values(action.cases)) {
        const caseActions = (caseEntry as { actions?: Record<string, Action> }).actions;
        if (caseActions) {
          const found = findForeachAction(caseActions, targetName);
          if (found) return found;
        }
      }
      if (action.default?.actions) {
        const found = findForeachAction(action.default.actions, targetName);
        if (found) return found;
      }
    }

    if (isUntilAction(action) && action.actions) {
      const found = findForeachAction(action.actions, targetName);
      if (found) return found;
    }

    if (isAgentAction(action) && action.tools) {
      for (const tool of Object.values(action.tools) as AgentTool[]) {
        if (tool.actions) {
          const found = findForeachAction(tool.actions, targetName);
          if (found) return found;
        }
      }
    }
  }

  return undefined;
};

/**
 * Pure utility: returns the number of child actions inside a ForEach action.
 * Searches recursively through nested scopes to find the target ForEach.
 */
export const getForEachChildCount = (
  actions: Record<string, Action> | undefined,
  foreachActionName: string
): number => {
  if (!actions) return 0;

  const foreachAction = findForeachAction(actions, foreachActionName);
  if (!foreachAction?.actions) return 0;

  return Object.keys(foreachAction.actions).length;
};

/**
 * Returns whether a ForEach action (looked up by name) is already at its
 * capacity. ForEach supports multiple child actions chained via runAfter.
 * This always returns false since there is no single-action limit.
 */
export function isForEachFull(
  _actions: Record<string, Action> | undefined,
  _foreachActionName: string,
): boolean {
  return false;
}

/**
 * Hook that returns constraint information for a ForEach action,
 * indicating whether it already has a child action and whether
 * a new action can be added.
 */
export function useForEachConstraint(
  foreachActionName: string
): ForEachConstraintResult {
  const actions = useWorkflowStore(
    (state) => state.workflow?.definition?.actions
  );

  return useMemo(() => {
    if (!actions) return EMPTY_CONSTRAINT;

    const foreachAction = findForeachAction(actions, foreachActionName);
    if (!foreachAction) return EMPTY_CONSTRAINT;

    const childNames = Object.keys(foreachAction.actions ?? {});
    const childActionCount = childNames.length;
    const hasAction = childActionCount > 0;

    return {
      hasAction,
      canAddAction: !hasAction,
      childActionName: childNames[0],
      childActionCount,
    };
  }, [actions, foreachActionName]);
}
